import { MatchingProduct } from '../Types/MatchingProducts.ts';
import { callProductListing } from '../../FitAssistantShared/ProductListingGlobals.ts';
import { fetchDataForAddToCartButtonInBatches } from '/Foundation/Common/code/Utils/Extranet/Utils.js';
import {
  addToCart,
  recalcLineTotal,
  setupLineTotal,
  setupQuantityStepper,
} from '/Foundation/Common/code/Views/Foundation/Common/AddToCartButtonIncQuantityInput.js';
import { LOCAL_STORAGE, ROLE_GUIDS } from '/Foundation/React/code/Utilities/ExtranetConstants.js';
import { safeGetItem } from '/Foundation/React/code/Utilities/LocalStorage.js';

// A roof rack family on the extranet product listing has no item of its own to buy. Once the fit assistant has a car,
// a confirmed card prices that car's solution through M3 and adds the solution's items to the cart.

type BatchResponse = {
  TotalSalesPrice: string;
  UnformattedTotalSalesPrice: number;
  HasInvalidItems: boolean;
};

const pendingSolutions = new WeakMap<HTMLElement, MatchingProduct>();
const requests = new WeakMap<HTMLElement, AbortController>();
let observer: IntersectionObserver | null = null;

const getArticle = (card: HTMLElement) => card.querySelector<HTMLElement>('.product-list-item');

const isSolutionCard = (card: HTMLElement): boolean => {
  if (!window._THULEDATA?.isExtranet) return false;
  const article = getArticle(card);
  return article?.dataset.producttype?.toLowerCase() === 'roof rack family' && !!article.querySelector('.buy-area');
};

const getTranslations = () => window._THULEDATA?.TopNavigation?.Translations;

const setSpinner = (article: HTMLElement, visible: boolean) =>
  article.querySelector('.buy-area > .loading-spinner')?.classList.toggle('d-none', !visible);

const showMessage = (article: HTMLElement, message: string | undefined) => {
  setSpinner(article, false);
  if (!message) return;

  const element = document.createElement('p');
  element.className = 'error-message solution-message';
  element.textContent = message;
  article.querySelector('.buy-area')?.append(element);
  callProductListing('setCardHeight');
};

const showOptionsLink = (card: HTMLElement, article: HTMLElement, count: number) => {
  const label = window._THULEDATA?.FitAssistant?.Translations?.matchingSolutions;
  const href = card.querySelector<HTMLAnchorElement>('a.product-link')?.href;
  if (!label || !href) return;

  const link = document.createElement('a');
  link.className = 'solution-options paragraph--xs text-decoration-underline';
  link.href = href;
  link.textContent = `${label} (${count})`;
  article.querySelector('.buy-area')?.append(link);
};

const showSolution = (card: HTMLElement, article: HTMLElement, solution: MatchingProduct, data: BatchResponse) => {
  setSpinner(article, false);

  if (data.HasInvalidItems) {
    showMessage(article, getTranslations()?.MultipleProductErrors ?? getTranslations()?.GeneralErrorText);
    return;
  }

  const priceValue = article.querySelector('.price-value');
  if (priceValue) priceValue.innerHTML = data.TotalSalesPrice;
  article.dataset.unitSalesPrice = String(data.UnformattedTotalSalesPrice);
  recalcLineTotal(article);

  const roleId = safeGetItem(LOCAL_STORAGE.EXTRANET_USER)?.HighestRole?.Id;
  const button = article.querySelector<HTMLButtonElement>('.add-to-cart-btn');
  if (button) {
    // A fresh button, so the click handler of an earlier car's solution is gone.
    const freshButton = button.cloneNode(true) as HTMLButtonElement;
    button.replaceWith(freshButton);

    if (roleId === ROLE_GUIDS.DEALER_HIDE_PRICES) {
      freshButton.disabled = true;
    } else {
      freshButton.addEventListener('click', (e) => {
        const quantity = article.querySelector<HTMLInputElement>('.quantity')?.value || '1';
        const products = (solution.SolutionItems ?? []).map((item) => ({ ProductId: item.ProductId, Quantity: 1 }));
        addToCart(e, products, quantity, article.dataset.productname, e.currentTarget);
      });
    }

    if (roleId === ROLE_GUIDS.CUSTOMER_ORDER_REGISTRATION_USER) {
      freshButton.classList.add('d-none');
      article.querySelector('.quantity')?.classList.add('d-none');
    }
  }

  article.querySelector('.product-inc-quantity-add-to-cart')?.classList.remove('d-none');
  article.querySelector('.add-to-your-cart')?.classList.remove('d-none');

  if ((solution.SolutionOptionCount ?? 0) > 1) showOptionsLink(card, article, solution.SolutionOptionCount!);
  callProductListing('setCardHeight');
};

const loadSolution = (card: HTMLElement, solution: MatchingProduct) => {
  const article = getArticle(card);
  if (!article) return;

  setupQuantityStepper(article);
  setupLineTotal(article);
  // A solution has no single stock status or delivery date to show.
  article.querySelector('.buy-area .delivery-date')?.remove();

  const items = solution.SolutionItems ?? [];
  if (items.some((item) => !item.ErpOutdoor)) {
    showMessage(article, getTranslations()?.GeneralErrorText);
    return;
  }

  // M3 prices each item number once, with how many of it the solution needs.
  const quantities = new Map<string, number>();
  items.forEach((item) => quantities.set(item.ErpOutdoor!, (quantities.get(item.ErpOutdoor!) ?? 0) + 1));
  const batchItems = [...quantities].map(([itemNumber, quantity]) => ({ itemNumber, productType: '', quantity }));

  const controller = new AbortController();
  requests.set(card, controller);
  setSpinner(article, true);

  const request = fetchDataForAddToCartButtonInBatches(
    batchItems,
    (data: BatchResponse) => {
      if (controller.signal.aborted) return;
      requests.delete(card);
      showSolution(card, article, solution, data);
    },
    (error: unknown) => {
      if (controller.signal.aborted) return;
      requests.delete(card);
      console.error('[SolutionBuyArea] Could not price the solution', error);
      showMessage(article, getTranslations()?.GeneralErrorText);
    },
    '',
    undefined,
    controller.signal
  );

  // No selected retailer, so nothing to price against.
  if (!request) setSpinner(article, false);
};

const getObserver = (): IntersectionObserver => {
  observer ??= new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        const card = entry.target as HTMLElement;
        observer?.unobserve(card);
        const solution = pendingSolutions.get(card);
        pendingSolutions.delete(card);
        if (solution) loadSolution(card, solution);
      });
    },
    // Same idea as the listing's own M3 loading: only cards near the viewport call M3.
    { rootMargin: '300px 0px' }
  );
  return observer;
};

export const resetSolutionBuyArea = (card: HTMLElement): void => {
  if (!isSolutionCard(card)) return;
  const article = getArticle(card)!;

  pendingSolutions.delete(card);
  observer?.unobserve(card);
  requests.get(card)?.abort();
  requests.delete(card);

  article.querySelectorAll('.solution-message, .solution-options').forEach((element) => element.remove());
  article.querySelector('.product-inc-quantity-add-to-cart')?.classList.add('d-none');
  setSpinner(article, false);

  const priceValue = article.querySelector('.price-value');
  if (priceValue) priceValue.innerHTML = '';
  delete article.dataset.unitSalesPrice;
  recalcLineTotal(article);
};

export const updateSolutionBuyArea = (card: HTMLElement, solution: MatchingProduct | undefined): void => {
  if (!isSolutionCard(card)) return;

  resetSolutionBuyArea(card);
  if (!solution?.SolutionItems?.length) return;

  pendingSolutions.set(card, solution);
  getObserver().observe(card);
};
